"use client";

import {
  Camera,
  CameraOff,
  Check,
  Maximize2,
  MessageSquare,
  Mic,
  MicOff,
  Minimize2,
  Paperclip,
  Pause,
  Play,
  Send,
  X,
  CalendarDays,
  UserRoundSearch,
  Video,
} from "lucide-react";
import { format } from "date-fns";
import { srLatn } from "date-fns/locale";
import { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";

import { Button } from "~/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { CameraOffPlaceholder } from "~/components/chat/components/dailyjs/camera-off-placeholder";
import { MessageBubble } from "~/components/chat/components/message-bubble";
import { SessionCard } from "~/components/chat/components/session-card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";
import { capturePostHogEvent } from "~/lib/posthog/client";

const CAMERA_CUE_SEEN_STORAGE_KEY = "therapy-call-preview-camera-cue-seen";
const PLAYBACK_STATE_STORAGE_KEY = "therapy-call-preview-playback-state";
const THERAPIST_VIDEO_THUMBNAIL =
  "/images/homepage/therapy-call-participant-poster.jpg?v=purchased-20260724";
const THERAPIST_NAME = "Kristina Petković";

type PermissionRequest = "camera" | "microphone";
type PlaybackState = "playing" | "paused";

function SessionBookedIcon({ className }: { className?: string }) {
  return (
    <span
      className={cn("relative inline-flex h-4 w-4 shrink-0", className)}
      aria-hidden="true"
    >
      <CalendarDays className="h-4 w-4" strokeWidth={2} />
      <Check
        className="absolute -bottom-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-secondary p-px"
        strokeWidth={3}
      />
    </span>
  );
}

function getPreviewSessionLabel() {
  const currentHour = new Date();
  currentHour.setMinutes(0, 0, 0);

  return format(currentHour, "EEEE, d. MMMM 'u' HH:mm", {
    locale: srLatn,
  });
}

export function TherapyCallPreview({
  layout = "responsive",
  surface = "mist",
  autoPlay = false,
}: {
  layout?: "responsive" | "landscape" | "fill";
  surface?: "mist" | "transparent";
  autoPlay?: boolean;
}) {
  const session = useSession();
  const callPreviewRef = useRef<HTMLDivElement>(null);
  const stockVideoRef = useRef<HTMLVideoElement>(null);
  const selfVideoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  const isMountedRef = useRef(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPauseOverlayVisible, setIsPauseOverlayVisible] = useState(true);
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [draftMessage, setDraftMessage] = useState("");
  const [sentMessages, setSentMessages] = useState<string[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previewSessionLabel, setPreviewSessionLabel] = useState<string | null>(
    null,
  );
  const [permissionRequest, setPermissionRequest] =
    useState<PermissionRequest | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [shouldAnimateCamera, setShouldAnimateCamera] = useState(false);
  const userName = session.data?.user.name?.trim() ?? "";
  const userImage = session.data?.user.image ?? undefined;
  const userInitials =
    userName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "Ti";

  useEffect(() => {
    const video = stockVideoRef.current;
    if (!video) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const syncPlayback = () => {
      let storedPlaybackState: PlaybackState | null = null;

      try {
        const storedValue = window.localStorage.getItem(
          PLAYBACK_STATE_STORAGE_KEY,
        );
        storedPlaybackState =
          storedValue === "playing" || storedValue === "paused"
            ? storedValue
            : null;
      } catch {
        // The preview falls back to its default behavior when storage is unavailable.
      }

      if (
        reducedMotion.matches ||
        (!autoPlay && storedPlaybackState === "paused")
      ) {
        video.pause();
        setIsPlaying(false);
        return;
      }

      void video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    };

    syncPlayback();
    reducedMotion.addEventListener("change", syncPlayback);

    return () => {
      reducedMotion.removeEventListener("change", syncPlayback);
    };
  }, [autoPlay]);

  const persistPlaybackState = (state: PlaybackState) => {
    try {
      window.localStorage.setItem(PLAYBACK_STATE_STORAGE_KEY, state);
    } catch {
      // Playback controls remain usable when browser storage is unavailable.
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
      microphoneStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      setIsPauseOverlayVisible(true);
      return;
    }

    const hideOverlayTimeout = window.setTimeout(() => {
      setIsPauseOverlayVisible(false);
    }, 200);

    return () => window.clearTimeout(hideOverlayTimeout);
  }, [isPlaying]);

  useEffect(() => {
    try {
      setShouldAnimateCamera(
        window.localStorage.getItem(CAMERA_CUE_SEEN_STORAGE_KEY) === null,
      );
    } catch {
      setShouldAnimateCamera(true);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === callPreviewRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const updateSessionLabel = () => {
      setPreviewSessionLabel(getPreviewSessionLabel());
    };

    updateSessionLabel();

    const millisecondsUntilNextHour =
      60 * 60 * 1000 - (Date.now() % (60 * 60 * 1000));
    let intervalId: number | null = null;
    const timeoutId = window.setTimeout(() => {
      updateSessionLabel();
      intervalId = window.setInterval(updateSessionLabel, 60 * 60 * 1000);
    }, millisecondsUntilNextHour);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== null) window.clearInterval(intervalId);
    };
  }, []);

  const togglePlayback = (control: "surface" | "toolbar") => {
    const video = stockVideoRef.current;
    if (!video) return;

    if (!isPlaying) {
      void video
        .play()
        .then(() => {
          setIsPlaying(true);
          persistPlaybackState("playing");
          capturePostHogEvent("homepage_demo_interacted", {
            interaction: "play",
            control,
          });
        })
        .catch(() => setIsPlaying(false));
      return;
    }

    video.pause();
    setIsPlaying(false);
    persistPlaybackState("paused");
    capturePostHogEvent("homepage_demo_interacted", {
      interaction: "pause",
      control,
    });
  };

  const toggleFullscreen = async () => {
    const preview = callPreviewRef.current;
    if (!preview) return;

    try {
      if (document.fullscreenElement === preview) {
        await document.exitFullscreen();
      } else {
        await preview.requestFullscreen();
        capturePostHogEvent("homepage_demo_interacted", {
          interaction: "fullscreen",
        });
      }
    } catch {
      // Browsers can reject fullscreen outside a trusted user gesture.
    }
  };

  const toggleChat = () => {
    setIsChatOpen((open) => {
      if (!open) {
        capturePostHogEvent("homepage_demo_interacted", {
          interaction: "chat_opened",
        });
      }

      return !open;
    });
  };

  const sendPreviewMessage = () => {
    const message = draftMessage.trim();
    if (!message) return;

    setSentMessages((messages) => [...messages, message]);
    setDraftMessage("");
  };

  const stopCamera = () => {
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
    if (selfVideoRef.current) {
      selfVideoRef.current.srcObject = null;
    }
    setIsCameraEnabled(false);
  };

  const stopMicrophone = () => {
    microphoneStreamRef.current?.getTracks().forEach((track) => track.stop());
    microphoneStreamRef.current = null;
    setIsMicrophoneEnabled(false);
  };

  const openPermissionRequest = (request: PermissionRequest) => {
    if (request === "camera") {
      setShouldAnimateCamera(false);
      try {
        window.localStorage.setItem(CAMERA_CUE_SEEN_STORAGE_KEY, "true");
      } catch {
        // The camera preview remains usable when browser storage is unavailable.
      }
    }

    setPermissionError(null);
    setPermissionRequest(request);
  };

  const confirmPermissionRequest = async () => {
    if (!permissionRequest) return;

    setIsRequestingPermission(true);
    setPermissionError(null);

    try {
      const isCameraRequest = permissionRequest === "camera";
      const stream = await navigator.mediaDevices.getUserMedia({
        video: isCameraRequest,
        audio: !isCameraRequest,
      });

      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      if (isCameraRequest) {
        stopCamera();
        cameraStreamRef.current = stream;
        if (selfVideoRef.current) {
          selfVideoRef.current.srcObject = stream;
          void selfVideoRef.current.play().catch(() => undefined);
        }
        setIsCameraEnabled(true);
      } else {
        stopMicrophone();
        microphoneStreamRef.current = stream;
        setIsMicrophoneEnabled(true);
      }

      setPermissionRequest(null);
    } catch (error) {
      setPermissionError(
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "Dozvola nije odobrena. Možeš je uključiti kasnije u podešavanjima pregledača."
          : "Nismo uspeli da pristupimo uređaju. Proveri da li je dostupan i pokušaj ponovo.",
      );
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && !isRequestingPermission) {
      setPermissionRequest(null);
      setPermissionError(null);
    }
  };

  return (
    <>
      <div
        className={cn(
          "relative w-full overflow-hidden",
          surface === "mist"
            ? "rounded-lg bg-secondary"
            : "rounded-none bg-transparent",
          layout === "fill"
            ? "h-full"
            : layout === "landscape"
              ? "aspect-video"
              : "aspect-9/16 sm:aspect-video",
        )}
      >
        {isPlaying && surface === "mist" && (
          <div className="absolute left-6 top-6 z-20 max-w-56 rounded-2xl bg-background/95 p-4 shadow-lg ring-1 ring-slate-900/5 backdrop-blur-xs sm:left-8 sm:top-8">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand">
              PRIMER RAZGOVORA
            </p>
            <p className="mt-1 text-base font-semibold text-foreground sm:text-lg">
              Ovako izgleda razgovor sa terapeutom.
            </p>
          </div>
        )}

        <div
          ref={callPreviewRef}
          className={cn(
            "fullscreen:fixed fullscreen:inset-0 fullscreen:h-screen fullscreen:w-screen fullscreen:rounded-none absolute overflow-hidden bg-slate-950 shadow-2xl ring-1 ring-black/10",
            surface === "transparent"
              ? "inset-0 rounded-none"
              : "inset-3 rounded-md sm:inset-4",
          )}
        >
          <video
            ref={stockVideoRef}
            onClick={() => togglePlayback("surface")}
            className="absolute inset-0 h-full w-full cursor-pointer object-cover object-center"
            muted
            loop
            playsInline
            preload="metadata"
            poster={THERAPIST_VIDEO_THUMBNAIL}
            aria-label="Primer online video razgovora"
          >
            <source
              src="/videos/homepage/therapy-call-participant.mp4?v=purchased-20260724"
              type="video/mp4"
            />
          </video>

          {isPauseOverlayVisible && (
            <div
              className={cn(
                "pointer-events-none absolute inset-0 z-5 flex items-center justify-center bg-slate-950/35 px-6 motion-safe:duration-200",
                isPlaying
                  ? "motion-safe:animate-out motion-safe:fade-out-0"
                  : "motion-safe:animate-in motion-safe:fade-in-0",
              )}
              aria-hidden="true"
            >
              <div
                className={cn(
                  "w-full max-w-76 px-2 text-center text-white motion-safe:duration-300 sm:max-w-md",
                  isPlaying
                    ? "motion-safe:animate-out motion-safe:fade-out-0 motion-safe:slide-out-to-bottom-2"
                    : "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2",
                )}
              >
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-secondary">
                  Sve za terapiju, na jednom mestu
                </p>
                <p className="mt-1 text-base font-semibold leading-snug sm:text-lg">
                  Pronađi terapeuta, zakaži termin i razgovaraj online.
                </p>
                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-white/25 pt-3">
                  <div className="flex flex-col items-center gap-1 text-[0.65rem] font-medium text-white/85 sm:text-xs">
                    <UserRoundSearch className="h-4 w-4 text-secondary" />
                    Terapeuti
                  </div>
                  <div className="flex flex-col items-center gap-1 text-[0.65rem] font-medium text-white/85 sm:text-xs">
                    <CalendarDays className="h-4 w-4 text-secondary" />
                    Termini
                  </div>
                  <div className="flex flex-col items-center gap-1 text-[0.65rem] font-medium text-white/85 sm:text-xs">
                    <Video className="h-4 w-4 text-secondary" />
                    Video razgovor
                  </div>
                </div>
              </div>
            </div>
          )}

          {isChatOpen && (
            <aside
              aria-label="Primer poruka"
              className="absolute inset-y-0 right-0 z-30 flex w-[90%] flex-col border-l border-slate-200 bg-background text-foreground shadow-2xl sm:w-[min(21rem,calc(100%-1rem))]"
            >
              <div className="flex items-center justify-between border-b bg-background px-4 py-2 sm:hidden">
                <h2 className="text-base font-semibold">Poruke</h2>
                <button
                  type="button"
                  onClick={() => setIsChatOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Zatvori poruke na mobilnom"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex min-w-0 items-center gap-2 border-b px-2 py-2.5 sm:px-4">
                <div className="shrink-0 self-center">
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={THERAPIST_VIDEO_THUMBNAIL}
                      alt={THERAPIST_NAME}
                    />
                    <AvatarFallback className="bg-primary/10 text-sm font-medium text-primary">
                      K
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                  <span className="line-clamp-1 text-[15px] font-semibold leading-tight">
                    {THERAPIST_NAME}
                  </span>
                  <span className="text-xs leading-tight text-muted-foreground">
                    Psihoterapeut
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsChatOpen(false)}
                  className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:flex"
                  aria-label="Zatvori poruke"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain whitespace-pre-wrap px-3 py-4 text-sm">
                <div className="flex min-h-full flex-col justify-end gap-4">
                  <SessionCard
                    icon={SessionBookedIcon}
                    title="Uvodni razgovor je zakazan"
                    description="Sve je spremno za uvodni razgovor."
                  >
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                      <span className="capitalize">
                        {previewSessionLabel ?? "Sada"}, online
                      </span>
                    </div>
                  </SessionCard>
                  <MessageBubble
                    senderName="Terapeut"
                    senderImage={THERAPIST_VIDEO_THUMBNAIL}
                  >
                    Zdravo! Vidim da je uvodni razgovor zakazan. Ako želiš nešto
                    da pitaš pre termina, slobodno piši. Vidimo se :)
                  </MessageBubble>
                  <MessageBubble isMine senderName="Ti" avatarVariant="user">
                    Hvala, vidimo se! 😊
                  </MessageBubble>
                  <SessionCard
                    icon={Video}
                    iconClassName="bg-green-100 text-brand"
                    borderClassName="border-green-300"
                    title="Sesija je u toku"
                    description={
                      <>
                        Preostalo još 45 min
                        <span className="ml-2 inline-flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                          Kristina je u sobi
                        </span>
                      </>
                    }
                    footer={
                      <span className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
                        <Video className="h-4 w-4" />
                        Pridruži se sesiji
                      </span>
                    }
                  />
                  {sentMessages.map((message, index) => (
                    <MessageBubble
                      key={`${message}-${index}`}
                      isMine
                      senderName="Ti"
                      avatarVariant="user"
                    >
                      {message}
                    </MessageBubble>
                  ))}
                </div>
              </div>

              <form
                className="border-t bg-background p-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  sendPreviewMessage();
                }}
              >
                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground"
                    aria-label="Dodaj prilog"
                  >
                    <Paperclip className="h-5 w-5" />
                  </button>
                  <div className="flex-1 rounded-2xl border bg-muted/30 px-3 py-2">
                    <input
                      value={draftMessage}
                      onChange={(event) => setDraftMessage(event.target.value)}
                      aria-label="Odgovor terapeutu Kristina Petković"
                      placeholder="Odgovor terapeutu Kristina Petković..."
                      className="w-full bg-transparent text-sm outline-hidden placeholder:text-muted-foreground"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!draftMessage.trim()}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Pošalji poruku"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </aside>
          )}

          <div
            className={cn(
              "absolute z-10 overflow-hidden shadow-lg ring-1 ring-white/15",
              surface === "transparent"
                ? "bottom-16 right-2 h-[72px] w-[54px] rounded-md sm:bottom-14 sm:right-3 sm:h-[90px] sm:w-[135px] sm:rounded-lg"
                : "bottom-20 right-2 h-[100px] w-[75px] rounded-lg sm:right-3 sm:h-[120px] sm:w-[200px] sm:rounded-xl",
            )}
          >
            <video
              ref={selfVideoRef}
              autoPlay
              muted
              playsInline
              className={cn(
                "absolute inset-0 h-full w-full object-cover",
                !isCameraEnabled && "hidden",
              )}
              aria-label="Tvoj lokalni pregled kamere"
            />
            {!isCameraEnabled && (
              <CameraOffPlaceholder
                userName={userName || "Ti"}
                avatarUrl={userImage}
              />
            )}
          </div>

          <div className="absolute inset-x-0 bottom-4 z-10 flex items-center justify-center gap-2 px-4">
            <div className="flex items-center gap-2 rounded-2xl bg-slate-950/75 p-2 shadow-xl ring-1 ring-white/10 backdrop-blur-xs">
              <button
                type="button"
                onClick={() =>
                  isMicrophoneEnabled
                    ? stopMicrophone()
                    : openPermissionRequest("microphone")
                }
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-xl border text-white transition-colors",
                  isMicrophoneEnabled
                    ? "border-secondary/50 bg-brand-action"
                    : "border-red-500/30 bg-red-500/20 text-red-400 hover:bg-red-500/30",
                )}
                aria-label={
                  isMicrophoneEnabled ? "Isključi mikrofon" : "Uključi mikrofon"
                }
              >
                {isMicrophoneEnabled ? (
                  <Mic className="h-4 w-4" />
                ) : (
                  <MicOff className="h-4 w-4" />
                )}
              </button>
              <button
                type="button"
                onClick={() =>
                  isCameraEnabled
                    ? stopCamera()
                    : openPermissionRequest("camera")
                }
                className={cn(
                  "relative flex h-9 w-9 items-center justify-center rounded-xl border text-white transition-colors",
                  isCameraEnabled
                    ? "border-secondary/50 bg-brand-action"
                    : "border-red-500/30 bg-red-500/20 text-red-400 hover:bg-red-500/30",
                )}
                aria-label={
                  isCameraEnabled ? "Isključi kameru" : "Uključi kameru"
                }
              >
                {!isCameraEnabled && shouldAnimateCamera && (
                  <span
                    aria-hidden="true"
                    data-testid="camera-attention-cue"
                    className="pointer-events-none absolute -inset-1 rounded-2xl border-2 border-secondary/80 motion-safe:animate-ping"
                  />
                )}
                {isCameraEnabled ? (
                  <Camera className="relative h-4 w-4" />
                ) : (
                  <CameraOff className="relative h-4 w-4" />
                )}
              </button>
              <button
                type="button"
                onClick={toggleChat}
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-xl border transition-colors",
                  isChatOpen
                    ? "border-secondary/50 bg-brand-action text-white"
                    : "border-white/15 bg-white/10 text-white hover:bg-white/20",
                )}
                aria-label={isChatOpen ? "Zatvori poruke" : "Otvori poruke"}
                aria-expanded={isChatOpen}
              >
                <MessageSquare className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => void toggleFullscreen()}
                className="hidden h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white transition-colors hover:bg-white/20 sm:flex"
                aria-label={
                  isFullscreen ? "Izađi iz celog ekrana" : "Ceo ekran"
                }
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>
              <button
                type="button"
                onClick={() => togglePlayback("toolbar")}
                className={cn(
                  "flex h-9 w-11 items-center justify-center rounded-xl text-white transition-colors",
                  isPlaying
                    ? "bg-white/15 hover:bg-white/25"
                    : "bg-brand-action hover:bg-brand-action-hover",
                )}
                aria-label={isPlaying ? "Pauziraj prikaz" : "Pokreni prikaz"}
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="ml-0.5 h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={permissionRequest !== null}
        onOpenChange={handleDialogOpenChange}
      >
        <DialogContent className="max-w-[calc(100%-2rem)] rounded-2xl border-slate-200 shadow-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {permissionRequest === "camera"
                ? "Proveri kako izgledaš"
                : "Proveri kako zvučiš"}
            </DialogTitle>
            <DialogDescription>
              {permissionRequest === "camera"
                ? "Ovo je samo demo, ništa se ne snima niti čuva. Uključi kameru da vidiš svoj prikaz, baš kao na pravoj sesiji."
                : "Ovo je samo demo, ništa se ne snima niti čuva. Uključi mikrofon da proveriš zvuk, baš kao na pravoj sesiji."}
            </DialogDescription>
          </DialogHeader>

          {permissionError && (
            <p
              role="alert"
              className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {permissionError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isRequestingPermission}
            >
              Ne sada
            </Button>
            <Button
              type="button"
              variant="cta"
              onClick={confirmPermissionRequest}
              disabled={isRequestingPermission}
              isLoading={isRequestingPermission}
            >
              {permissionRequest === "camera"
                ? "Uključi kameru"
                : "Uključi mikrofon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
